import { GlobalFilter } from "@volt_developers/components";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { COMMON_TRANSLATION_KEYS } from "constants/commonTranslationsKeys";
import { useGlobalFilter } from "hooks/useGlobalFilter";
import { useOnScreen } from "hooks/useOnScreen";
import { isElementInFourthQuartile } from "utils/isElementInFourthQuartile";

import { TEST_IDS } from "../../../constants/TestIds";

import type {
  SearchResultCustomer,
  SearchResults,
} from "./useGlobalFilterSearch";

export interface GlobalFilterSearchListProps {
  searchResults: SearchResults;
  isLoading: boolean;
  handleSelectCustomer: (selectedCustomer: SearchResultCustomer) => void;
  handleLoadMore: VoidFunction;
  hasMore: boolean;
}

export function GlobalFilterSearchList({
  hasMore,
  isLoading,
  searchResults,
  handleSelectCustomer,
  handleLoadMore,
}: GlobalFilterSearchListProps) {
  const { t } = useTranslation("common");
  const elementRef = useRef(null);
  const { globallyFilteredCustomer } = useGlobalFilter();

  const isElementVisible = useOnScreen(elementRef, searchResults.length);

  useEffect(() => {
    if (isElementVisible) {
      handleLoadMore();
    }
  }, [handleLoadMore, isElementVisible]);

  if (!Boolean(searchResults.length) || isLoading) {
    return SKELETON_LIST;
  }

  return (
    <GlobalFilter.Menu data-testid={TEST_IDS.globalFilterSearchList}>
      {searchResults.map(({ id, name, descendantsCount }, idx) => (
        <GlobalFilter.Item
          key={`global-filter-search-${id}`}
          data-testid={TEST_IDS.globalFilterSearchItem}
          id={
            Boolean(descendantsCount)
              ? t(COMMON_TRANSLATION_KEYS.accounts, {
                  count: descendantsCount,
                })
              : id
          }
          customer={name}
          ref={
            isElementInFourthQuartile(idx + 1, searchResults.length)
              ? elementRef
              : undefined
          }
          isDisabled={globallyFilteredCustomer?.id === id}
          isFavoriteVisible={false}
          onClick={() =>
            handleSelectCustomer({
              id,
              name,
              descendantsCount,
            })
          }
        />
      ))}
      {hasMore && <GlobalFilter.ItemSkeleton />}
    </GlobalFilter.Menu>
  );
}

export const SKELETON_LIST_LENGTH = 4;
const SKELETON_LIST = (
  <div data-testid={TEST_IDS.globalFilterSearchSkeletonList}>
    {Array.from({ length: SKELETON_LIST_LENGTH }, (_, idx) => (
      <GlobalFilter.ItemSkeleton key={idx} />
    ))}
  </div>
);
